import React from 'react';
import { useLanguage } from '@/contexts';

interface CopyButtonProps {
  onCopy: () => void;
  label?: string;
  size?: 'sm' | 'md';
  variant?: 'icon-only' | 'with-text';
  className?: string;
}

// Simple copy icon component
const CopyIcon: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'md' }) => {
  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  
  return (
    <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
};

// Small copy button for content headers
const SmallCopyButton: React.FC<{ onCopy: () => void; label: string }> = ({ onCopy, label }) => {
  const { t } = useLanguage();
  return (
  <button
    onClick={onCopy}
    className="flex items-center text-gray-500 hover:text-gray-700 text-xs"
    aria-label={`Copy ${label.toLowerCase()}`}
  >
    <CopyIcon size="sm" />
    <span className="ml-1">{t("copy")}</span>
  </button>
)};

// Large copy button for forms/actions
const LargeCopyButton: React.FC<{ onCopy: () => void; label: string; className?: string }> = ({ 
  onCopy, 
  label, 
  className = "" 
}) => (
  <button
    onClick={onCopy}
    aria-label={`Copy ${label.toLowerCase()}`}
    className={`w-full px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue ${className}`}
  >
    {label}
  </button>
);

// Main flexible copy button component
const CopyButton: React.FC<CopyButtonProps> = ({ 
  onCopy, 
  label = "Copy", 
  size = 'md',
  variant = 'with-text',
  className = ""
}) => {
  if (variant === 'icon-only') {
    return (
      <button
        onClick={onCopy}
        className={`p-2 text-gray-500 hover:text-gray-700 ${className}`}
        aria-label={`Copy ${label.toLowerCase()}`}
      >
        <CopyIcon size={size} />
      </button>
    );
  }

  return (
    <button
      onClick={onCopy}
      className={`flex items-center text-gray-500 hover:text-gray-700 ${size === 'sm' ? 'text-xs' : 'text-sm'} ${className}`}
      aria-label={`Copy ${label.toLowerCase()}`}
    >
      <CopyIcon size={size} />
      <span className="ml-1">{label}</span>
    </button>
  );
};

export default CopyButton;

// Export specialized variants for direct use
export { CopyIcon, SmallCopyButton, LargeCopyButton };
